import ApiRequestManagerInstance from '../ApiRequestManager';
import EpgEvent from '../../classes/epgEvent';
import { setUpdateRate } from '@shared/store/slices/events.slice';
import { store } from '@shared/store/store';
import {
  EventMap,
  IEPGEventRequest,
  IEventsRequest,
} from '../../types/interfaces/event';

const HOUR_OFFSET = 3;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;

/**
 * Make request to get events for the next three hours gap
 * @param date - Date to make request for
 * @returns Promise with events
 */
async function nextHoursBaseRequest(date: Date) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  let hour = date.getUTCHours();
  hour = hour - (hour % HOUR_OFFSET); // Round to closest available hour
  const finalHour = `${hour < 10 ? 0 : ''}${hour}`;

  const promise = new Promise<EventMap>(async (resolve, reject) => {
    try {
      // Make request
      const response: IEventsRequest = (await ApiRequestManagerInstance.get({
        endpoint: {
          url: 'cdnSyncUrl',
          section: 'media',
          subsection: 'carrier',
          type: 'nextHoursEvents',
          params: `${year}/${month}/${day}/${finalHour}.json`,
        },
      })) as unknown as IEventsRequest;

      // Filter events by channels
      const {
        channels: { channelsData },
      } = store.getState();

      const events: EventMap = {};
      response.data
        .filter((event) =>
          channelsData?.find((channel) => event.channel === channel.id)
        )
        .map((value: IEPGEventRequest) => {
          return new EpgEvent(value);
        })
        .sort((item1, item2) => {
          if ((item1?.date?.start || 0) > (item2?.date?.start || 0)) {
            return 1;
          }
          return -1;
        })
        .forEach((value: EpgEvent) => {
          if (!events[value.channelId]) {
            events[value.channelId] = [];
          }
          events[value.channelId].push(value);
        });

      // Sort events by start time
      Object.keys(events).forEach((key) => {
        events[key] = events[key].sort((item1, item2) => {
          if ((item1?.date?.start || 0) > (item2?.date?.start || 0)) {
            return 1;
          }
          return -1;
        });
      });

      // Resolve events
      resolve(events);
    } catch (error) {
      // Reject error
      console.error(error);
      reject(error);
    }
  });

  return promise;
}

/**
 * Make request to get events for the current three hours gap.
 * Fetches events for the next three hours gap if the remaining hours for the current gap is less than 1 hour.
 * @returns Promise with events
 */
export default function nextHoursEventsRequest() {
  const promises = [];
  const date = new Date();

  // Add current three hours gap events
  promises.push(nextHoursBaseRequest(date));

  // Add next three hours gap events if the remaining hours for the current gap is less than 1 hour
  if (date.getUTCHours() % 3 > 1) {
    promises.push(
      nextHoursBaseRequest(new Date(date.getTime() + 3 * 60 * 60 * 1000))
    );

    // Set accelerated update rate so that events are fetched before the end of the next required gap
    const remainingMinutes = MINUTES_PER_HOUR - date.getMinutes();

    // An hour and a half is substracted so that next request does not require to fetch two gaps at once
    const updateRate =
      ((HOUR_OFFSET - 1.5) * MINUTES_PER_HOUR + remainingMinutes) *
      SECONDS_PER_MINUTE *
      MILLISECONDS_PER_SECOND;
    store.dispatch(setUpdateRate(updateRate));
  }

  return promises;
}
